import { Link } from 'react-router-dom';
import { ShieldCheck, Receipt, ClipboardCheck, Wrench, ArrowRight } from 'lucide-react';

const ROLES = [
  {
    key: 'admin',
    to: '/admin',
    title: 'Super Admin',
    name: 'Danish Syed',
    icon: <ShieldCheck />,
    accent: 'warm',
    description: 'Org-wide oversight across all 7 shops. ACH payment queue. Escalations from auditor. Recovery trends.',
    tagline: 'Oversight',
  },
  {
    key: 'bookkeeper',
    to: '/bookkeeper',
    title: 'Bookkeeper',
    name: 'Patricia Gray',
    icon: <Receipt />,
    accent: 'insight',
    description: 'Weekly statement reconciliation. COD check entry from scanned PDFs. Bill pay queue. Weekly close checklist.',
    tagline: 'Bill pay + reconciliation',
  },
  {
    key: 'auditor',
    to: '/auditor',
    title: 'Auditor',
    name: 'Nadeem Nisar',
    icon: <ClipboardCheck />,
    accent: 'recovery',
    description: 'Daily audit queue. Parts to Return bucket with 7-day SLA. Daily audit email. Task verification. Exception rules.',
    tagline: 'The engine',
  },
  {
    key: 'store',
    to: '/store',
    title: 'Store User',
    name: 'Store Manager',
    icon: <Wrench />,
    accent: 'action',
    description: 'My tasks assigned by auditor. Shop-scoped issues. Daily digest from auditor. Mobile-first, fixes between repairs.',
    tagline: 'Fix & resolve',
  },
];

export default function RolePickerPage() {
  return (
    <div className="min-h-screen bg-[var(--color-cream-100)] font-sans flex items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        {/* Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-[var(--radius-md)] bg-[var(--color-warm-900)] text-[var(--color-cream-50)] flex items-center justify-center font-extrabold text-[18px] shadow-[var(--shadow-md)]">
              R
            </div>
          </div>
          <h1 className="text-[32px] font-extrabold text-[var(--color-warm-900)] tracking-tight leading-tight mb-2">
            Parts & Vendor Reconciliation Hub
          </h1>
          <p className="text-[14px] text-[var(--color-warm-600)]">
            Pick a role to preview its dashboard. Real auth lands in Phase B.
          </p>
        </div>

        {/* Role grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-children">
          {ROLES.map((role) => (
            <RoleCard key={role.key} role={role} />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-10 text-center">
          <div className="text-[12px] text-[var(--color-warm-500)] mb-1">
            Legacy dashboard still available at
          </div>
          <Link
            to="/"
            className="text-[12px] font-semibold text-[var(--color-warm-700)] hover:text-[var(--color-warm-900)] underline decoration-[var(--color-warm-300)] hover:decoration-[var(--color-warm-600)] underline-offset-2"
          >
            /  (old UI — will be removed after Phase F)
          </Link>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ role }) {
  const accents = {
    warm:     { bg: 'bg-[var(--color-cream-50)]', border: 'border-[var(--color-warm-100)]', icon: 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)]', tag: 'text-[var(--color-warm-700)]' },
    recovery: { bg: 'bg-[var(--color-recovery-50)]', border: 'border-[var(--color-recovery-100)]', icon: 'bg-[var(--color-recovery-500)] text-white', tag: 'text-[var(--color-recovery-700)]' },
    action:   { bg: 'bg-[var(--color-action-50)]', border: 'border-[var(--color-action-100)]', icon: 'bg-[var(--color-action-500)] text-white', tag: 'text-[var(--color-action-700)]' },
    insight:  { bg: 'bg-[var(--color-insight-50)]', border: 'border-[var(--color-insight-100)]', icon: 'bg-[var(--color-insight-500)] text-white', tag: 'text-[var(--color-insight-700)]' },
  };
  const a = accents[role.accent];

  return (
    <Link
      to={role.to}
      className={`
        group p-6 rounded-[var(--radius-xl)] border ${a.bg} ${a.border}
        hover:shadow-[var(--shadow-md)] hover:-translate-y-0.5
        transition-all duration-200 ease-out
        relative overflow-hidden
      `}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-12 h-12 rounded-[var(--radius-md)] ${a.icon} flex items-center justify-center shrink-0 shadow-[var(--shadow-sm)]`}>
          <span className="[&_svg]:w-6 [&_svg]:h-6">{role.icon}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[11px] font-bold uppercase tracking-[1px] ${a.tag} mb-1`}>
            {role.tagline}
          </div>
          <div className="text-[18px] font-extrabold text-[var(--color-warm-900)] tracking-tight">
            {role.title}
          </div>
          <div className="text-[12px] text-[var(--color-warm-500)]">
            Preview as {role.name}
          </div>
        </div>
      </div>

      <p className="text-[13px] text-[var(--color-warm-700)] leading-relaxed mb-4">
        {role.description}
      </p>

      <div className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--color-warm-900)] group-hover:gap-2.5 transition-all">
        Enter as {role.title}
        <ArrowRight size={14} />
      </div>
    </Link>
  );
}
